import React, { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import PortalLayout from "./PortalLayout";
import { StatCard, Panel, Badge, EmptyState, SkeletonCards } from "./BuyerPortal";
import SiteVisitsPanel from "@/components/SiteVisitsPanel";
import PartnerListings from "./PartnerListings";
import { useMembership, MembershipCard, PartnerTraining, PartnerBrands, Locked } from "./PartnerMembership";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    LayoutDashboard, Users, IndianRupee, Wallet, ClipboardCheck, MessageSquare,
    Package, FileText, Settings, TrendingUp, CalendarCheck, Building2, GraduationCap, Store,
} from "lucide-react";

const BASE_NAV = [
    { to: "/portal/partner",             l: "Dashboard",         icon: LayoutDashboard, end: true },
    { to: "/portal/partner/listings",    l: "My Listings",       icon: Building2 },
];
// Active Premium Channel Partners only (also enforced server-side)
const FULL_NAV = [
    { to: "/portal/partner/training",    l: "Training",          icon: GraduationCap },
    { to: "/portal/partner/ebg-brands",  l: "EBG Brands",        icon: Store },
    { to: "/portal/partner/leads",       l: "Lead Management",   icon: Users },
    { to: "/portal/partner/site-visits", l: "Site Visits",       icon: CalendarCheck },
    { to: "/portal/partner/bookings",    l: "My Bookings",       icon: ClipboardCheck },
    { to: "/portal/partner/commissions", l: "Commission Tracker",icon: Wallet },
    { to: "/portal/partner/inventory",   l: "Builder Inventory", icon: Package },
];
const PAID_PENDING_NAV = FULL_NAV.slice(0, 2);
const ACCOUNT_NAV = [
    { to: "/portal/partner/kyc",         l: "KYC & Docs",        icon: FileText },
    { to: "/portal/partner/support",     l: "Support",           icon: MessageSquare },
    { to: "/portal/partner/settings",    l: "Settings",          icon: Settings },
];

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function ChannelPartnerPortal() {
    const membership = useMembership();
    const full = !!membership?.paid_active;
    const middle = full ? FULL_NAV : membership?.partner_type === "PAID" ? PAID_PENDING_NAV : [];
    const nav = [...BASE_NAV, ...middle, ...ACCOUNT_NAV];
    const gate = (el, title, id) => (membership === null ? null : full ? el : <Locked title={title} testid={id}/>);
    return (
        <Routes>
            <Route element={<PortalLayout role="partner" brand="Channel Partner Portal" navItems={nav}/>}>
                <Route path="training" element={gate(<PartnerTraining/>, "Training", "partner-training")} />
                <Route path="ebg-brands" element={gate(<PartnerBrands/>, "EBG Brands", "partner-brands")} />
                <Route index element={<Dashboard membership={membership}/>} />
                <Route path="listings" element={<PartnerListings/>} />
                <Route path="leads" element={gate(<Leads/>, "Lead Management", "partner-leads")} />
                <Route path="site-visits" element={gate(<SiteVisitsPanel endpoint="/partner/site-visits" title="Upcoming Site Visits" scopeLabel="All site visits from active buyers across your territory. Coordinate with buyers to close the deal." testid="partner-site-visits"/>, "Site Visits", "partner-site-visits")} />
                <Route path="bookings" element={gate(<Bookings/>, "My Bookings", "partner-bookings")} />
                <Route path="commissions" element={gate(<Commissions/>, "Commission Tracker", "partner-commissions")} />
                <Route path="inventory" element={gate(<Inventory/>, "Builder Inventory", "partner-inventory")} />
                <Route path="kyc" element={<KYC/>} />
                <Route path="support" element={<Panel title="Support"><EmptyState msg="Raise a ticket by writing to info@ebgrealty.in."/></Panel>} />
                <Route path="settings" element={<Panel title="Settings"><p className="text-black/55 text-sm">Profile settings coming soon.</p></Panel>} />
            </Route>
        </Routes>
    );
}

function Dashboard({ membership }) {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/partner/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
    if (!data) return <SkeletonCards/>;
    const s = data.stats;
    return (
        <div className="space-y-6">
            <MembershipCard m={membership}/>
            {data.full_access && (
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="partner-full-stats">
                    <StatCard Icon={Users}          v={s.total_leads}             l="Total Leads" testid="stat-leads"/>
                    <StatCard Icon={ClipboardCheck} v={s.bookings}                l="Bookings"/>
                    <StatCard Icon={Wallet}         v={inr(s.commissions_paid)}    l="Commissions Paid"/>
                    <StatCard Icon={IndianRupee}    v={inr(s.commissions_pending)} l="Pending Payouts"/>
                </div>
            )}
            <Panel title="My Listings" cta={{ to: "/portal/partner/listings", l: "Manage Listings" }} testid="dashboard-listings-strip">
                <div className="grid grid-cols-3 gap-4">
                    <MiniStat label="Pending Review" v={s.listings_pending || 0}  tone="gold"/>
                    <MiniStat label="Approved"       v={s.listings_approved || 0} tone="green"/>
                    <MiniStat label="Rejected"       v={s.listings_rejected || 0} tone="red"/>
                </div>
                <p className="text-[12px] text-black/55 mt-4">Add properties from your inventory &mdash; each submission is reviewed within 1 business day before it goes live on the website.</p>
            </Panel>
        </div>
    );
}

function Leads() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/partner/leads").then((r) => setItems(r.data.items || [])).catch(() => {});
    useEffect(() => { load(); }, []);
    const setStatus = async (id, status) => {
        try { await api.patch(`/partner/leads/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const OPTS = ["new", "contacted", "site_visit", "negotiating", "won", "lost"];
    return (
        <Panel title="Lead Management" testid="leads-list">
            {items.length === 0 ? <EmptyState msg="No leads assigned yet. New leads geo-matched to your territory arrive within 24 hrs."/> : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px]">
                        <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                            <th className="pb-3">Name</th><th className="pb-3">Contact</th><th className="pb-3">Property</th><th className="pb-3">Status</th><th className="pb-3">Date</th>
                        </tr></thead>
                        <tbody>
                            {items.map((l) => (
                                <tr key={l.id} className="border-b border-black/8 last:border-b-0" data-testid={`partner-lead-row-${l.id}`}>
                                    <td className="py-3 font-bold">{l.name}</td>
                                    <td className="py-3 text-black/70 text-[12px]"><div>{l.email}</div><div className="text-black/50">{l.phone || ""}</div></td>
                                    <td className="py-3 text-black/70">{l.property_title || "—"}</td>
                                    <td className="py-3">
                                        <select value={l.status || "new"} onChange={(e) => setStatus(l.id, e.target.value)} data-testid={`partner-lead-status-${l.id}`} className="text-[11px] border border-black/15 px-2 py-1.5 bg-white outline-none focus:border-black">
                                            {OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </td>
                                    <td className="py-3 text-black/50 text-[12px]">{new Date(l.created_at).toLocaleDateString("en-IN")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Panel>
    );
}

function Bookings() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/partner/bookings").then((r) => setItems(r.data.items || [])); }, []);
    return (
        <Panel title="My Bookings" testid="bookings-list">
            {items.length === 0 ? <EmptyState msg="No confirmed bookings yet."/> : (
                <table className="w-full">
                    <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                        <th className="pb-3">Property</th><th className="pb-3">Buyer</th><th className="pb-3">Amount</th><th className="pb-3">Status</th>
                    </tr></thead>
                    <tbody>
                        {items.map((b) => (
                            <tr key={b.id} className="border-b border-black/8 last:border-b-0">
                                <td className="py-3">{b.property_title}</td>
                                <td className="py-3 text-black/70">{b.buyer_name}</td>
                                <td className="py-3 font-bold text-[var(--ebg-gold)]">{inr(b.amount)}</td>
                                <td className="py-3"><Badge tone="green">{b.status}</Badge></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </Panel>
    );
}

function Commissions() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/partner/commissions").then((r) => setItems(r.data.items || [])); }, []);
    const total = items.reduce((a, c) => a + (c.amount || 0), 0);
    const paid  = items.filter((c) => c.status === "paid").reduce((a, c) => a + c.amount, 0);
    const pend  = total - paid;
    return (
        <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
                <StatCard Icon={Wallet}      v={inr(total)} l="Total Earnings"/>
                <StatCard Icon={IndianRupee} v={inr(paid)}  l="Paid Out"      trend="Cleared"/>
                <StatCard Icon={TrendingUp}  v={inr(pend)}  l="Pending"       trend="This month"/>
            </div>
            <Panel title="Commission Ledger" testid="commissions-list">
                {items.length === 0 ? <EmptyState msg="No commissions yet."/> : (
                    <table className="w-full">
                        <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                            <th className="pb-3">Property</th><th className="pb-3">Amount</th><th className="pb-3">Status</th><th className="pb-3">Date</th>
                        </tr></thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} className="border-b border-black/8 last:border-b-0">
                                    <td className="py-3">{c.property_title}</td>
                                    <td className="py-3 font-bold text-[var(--ebg-gold)]">{inr(c.amount)}</td>
                                    <td className="py-3"><Badge tone={c.status === "paid" ? "green" : "gold"}>{c.status}</Badge></td>
                                    <td className="py-3 text-black/50 text-[12px]">{new Date(c.created_at).toLocaleDateString("en-IN")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Panel>
        </div>
    );
}

function Inventory() {
    return (
        <Panel title="Builder Inventory" cta={{ to: "/properties", l: "Explore Portfolio" }}>
            <p className="text-black/55 text-sm">Live inventory sync from partnered builders. Access unit-level availability, floor-wise pricing, and offers you can pass on to your clients — updated in real-time.</p>
        </Panel>
    );
}

function KYC() {
    const [form, setForm] = useState({ pan: "", aadhaar: "", rera_agent_id: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try { await api.post("/partner/kyc", form); toast.success("KYC submitted. Verification typically takes 24-48 hrs."); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <Panel title="KYC & Documents" testid="kyc-panel">
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
                <F l="PAN Number"        v={form.pan}          onChange={(v) => setForm({...form, pan: v})}/>
                <F l="Aadhaar Number"    v={form.aadhaar}      onChange={(v) => setForm({...form, aadhaar: v})}/>
                <F l="RERA Agent ID"     v={form.rera_agent_id} onChange={(v) => setForm({...form, rera_agent_id: v})}/>
                <F l="Notes (optional)"  v={form.notes}        onChange={(v) => setForm({...form, notes: v})}/>
                <button disabled={busy} className="md:col-span-2 bg-[var(--ebg-gold)] text-black py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-[var(--ebg-gold)] disabled:opacity-60" data-testid="kyc-submit">
                    {busy ? "Submitting…" : "Submit KYC"}
                </button>
            </form>
        </Panel>
    );
}

function F({ l, v, onChange }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">{l}</div>
            <input value={v} onChange={(e) => onChange(e.target.value)} className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
        </label>
    );
}

function MiniStat({ label, v, tone }) {
    const toneCls = tone === "green" ? "text-emerald-700 border-emerald-200 bg-emerald-50"
                  : tone === "red"   ? "text-red-700 border-red-200 bg-red-50"
                  :                    "text-black border-[var(--ebg-gold)]/40 bg-[var(--ebg-gold)]/10";
    return (
        <div className={`border ${toneCls} px-3 py-3`}>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">{label}</div>
            <div className="font-serif text-3xl leading-none mt-1">{v}</div>
        </div>
    );
}
